/* eslint-disable @typescript-eslint/no-explicit-any */
// FIXME: remove explicit any
import { useStackNavigate } from '@/app/navigation/stack-links';
import { ShareDialog } from '@/components/ShareDialog';
import { TableSearchBar } from '@/components/ui/TableSearchBar';
import { UnifiedContextMenu } from '@/components/ui/UnifiedContextMenu';
import { UnifiedAction } from '@/components/ui/UnifiedContextMenu/types';
import { ContentState } from '@/components/ui/data-table/TableControlsButton';
import { TableSelectionHeader } from '@/components/ui/data-table/TableSelectionHeader';
import {
  configToTableState,
  tableStateToConfig,
} from '@/components/ui/data-table/config';
import {
  MOBILE_SUMMARY_COLUMN_COUNT,
  MobileSummaryCell,
  MobileSummaryHeaderCell,
  useMobileSummaryWidth,
} from '@/components/ui/data-table/mobile';
import {
  TableBody,
  TableCell,
  Table as TableComponent,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { datatableLogger } from '@/config/logging';
import useDeepMemo from '@/hooks/use-deep-memo';
import { useIsMobile } from '@/hooks/use-mobile';
import { useShareDialog } from '@/hooks/use-share-dialog';
import { cn } from '@/lib/utils';
import { setterSideEffect } from '@/utils/setterSideEffect';
import { useNavigate } from '@tanstack/react-router';
import {
  ExpandedState,
  Row,
  TableFeature,
  TableState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  ChevronsDownUp,
  ChevronsUpDown,
  Share2,
  Square,
  SquareCheckBig,
} from 'lucide-react';
import {
  KeyboardEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { LoadingTableRow } from './LoadingTableRow';
import { NoDataTableRow } from './NoDataTableRow';
import {
  TREE_INDENT_PER_LEVEL_PX,
  treeLeadingPrefixPx,
} from './tree.constants';
import { setSubtreeExpanded } from './tree.utils';
import { StickyBulkActionBar } from './StickyBulkActionBar';
import { StickyScrollbar } from './StickyScrollbar';
import { TableColumnActionCell } from './TableActionColumnCell';
import { TableHeaderCell } from './TableHeaderCell';
import { DataTableProps, RowAction } from './types';

// TODO: DataTable: Natively support foreign key relations (e.g. project, user, label, etc)
// FIXME: Dont let implementation details bleed in to bulkEdit api
/* TODO: finalize entity-drag-n-drop.
 *   - dont use heuristict to determine type.
 *   - expose an opt in api to have the consumer configure this
 *   - use the summary cell as a single entity drag-preview
 */

const DataTableFeatures: TableFeature = {
  getInitialState: (iState) => {
    return { ...iState } as TableState;
  },
  getDefaultOptions() {
    return {
      enableSortingRemoval: false,
      enableSorting: true,
    };
  },
  getDefaultColumnDef() {
    return {
      enableSorting: true,
      enableMultiSort: true,
      sortingFn: 'auto',
    };
  },
  createColumn(column, table) {
    (column as any).toggleMultiSort = (/* dir?: SortDirection */) => {
      const idx = column.getSortIndex();
      if (idx === 0) {
        column.toggleSorting(undefined, true);
        return;
      }
      table.setSorting((sorting) => [
        { desc: false, id: column.id },
        ...sorting.filter(({ id }) => id !== column.id),
      ]);
    };
  },
};

const emptyArray: any[] = [];

export const DataTable = memo(InnerDataTable) as typeof InnerDataTable;

function InnerDataTable<TData, TValue>({
  columns,
  data = [],
  isLoading = false,
  createUriPath: createURIPath,
  createShareContent,
  config,
  onConfigChange,
  actions = emptyArray,
  renderBulkEdit,
  onNew,
  // Toolbar props
  filterValue,
  onFilterChange,
  tokenDefinitions,
  isFiltering,
  resultCount,
  totalCount,
  entityName,
  defaultFilter,
  addPath,
  addDisabled = false,
  // Row selection props
  rowSelection,
  onRowSelectionChange,
  getRowId,
  // Sorted data callback
  onSortedDataChange,
  // Row hover props
  hoveredRowId,
  onRowHover,
  activeRowId,
  // Tree mode (optional)
  tree,
}: DataTableProps<TData, TValue>) {
  const isMobile = useIsMobile();
  const summaryWidth = useMobileSummaryWidth(isMobile);

  if (import.meta.env.DEV) {
    datatableLogger.debug('DataTable rendering', {
      rowCount: data.length,
      columnCount: columns.length,
      isLoading,
      hasActions: actions.length > 0,
      isMobile,
    });
  }
  const [activeRowIndex, setActiveRowIndex] = useState<number | null>(null);

  let navigate: ReturnType<typeof useNavigate> = async (opts) => {
    console.log('navigate to', opts.to);
  };
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    navigate = useStackNavigate(); // stable enough
  } catch {
    /* noop */
  }

  // Resolve the tree row-click mode for the current viewport. When 'expand',
  // clicking an expandable row's body toggles expansion instead of drilling,
  // and a dedicated disclosure button in the actions cell carries the drill.
  const expandOnRowClick =
    !!tree &&
    (isMobile ? tree.rowClickAction?.mobile : tree.rowClickAction?.desktop) ===
      'expand';

  // Tree indent geometry, derived once from the shared constants so the
  // mobile summary row-2 pad matches the desktop leading indent. Zero when
  // not in tree mode.
  const treeIndentPerLevelPx = tree ? TREE_INDENT_PER_LEVEL_PX : 0;
  const treeLeadingPrefix = tree
    ? treeLeadingPrefixPx(tree.leadingHasIcon ?? true)
    : 0;

  const _columns: typeof columns = useMemo(
    () => [
      ...columns,
      {
        accessorKey: 'actions',
        enableHiding: false,
        enableMultiSort: false,
        enableSorting: false,
        header: () => null,
        cell: (props) => {
          // Show the drill disclosure button only for expandable rows the
          // consumer flags as having a separate drill target (see
          // tree.hasSecondaryDrill). Groups whose only action is expansion
          // get no button; leaf rows drill via row click, not this button.
          const showDrill =
            expandOnRowClick &&
            props.row.getCanExpand() &&
            (tree?.hasSecondaryDrill?.(props.row.original) ?? false);
          return (
            <TableColumnActionCell<TData, TValue>
              {...props}
              onDrill={
                showDrill
                  ? (data: TData) => navigate({ to: createURIPath(data) })
                  : undefined
              }
            />
          );
        },
      },
    ],
    [columns, createURIPath, expandOnRowClick, tree],
  );

  const initialState = useDeepMemo(() => {
    return configToTableState(config);
  }, [_columns, config]);

  const [sorting, setSorting] = useState(() => initialState.sorting);
  const [columnVisibility, setColumVisibility] = useState(
    initialState.columnVisibility,
  );
  const [columnOrder, setColumnOrder] = useState(initialState.columnOrder);
  const setSortingSideEffect = useCallback(
    setterSideEffect(setSorting, (newSorting) => {
      onConfigChange?.(
        tableStateToConfig({
          sorting: newSorting,
          columnVisibility,
          columnOrder,
        }),
      );
    }),
    [columnVisibility, columnOrder, onConfigChange],
  );
  const setColumVisibilitySideEffect = useCallback(
    setterSideEffect(setColumVisibility, (newVisibility) => {
      onConfigChange?.(
        tableStateToConfig({
          sorting,
          columnVisibility: newVisibility,
          columnOrder,
        }),
      );
    }),
    [sorting, columnOrder, onConfigChange],
  );
  const setColumnOrderSideEffect = useCallback(
    setterSideEffect(setColumnOrder, (newOrder) => {
      // Guard against undefined/empty order (can happen during column changes)
      if (!newOrder || newOrder.length === 0) return;
      onConfigChange?.(
        tableStateToConfig({
          sorting,
          columnVisibility,
          columnOrder: newOrder,
        }),
      );
    }),
    [sorting, columnVisibility, onConfigChange],
  );

  const [columnPinning] = useState({ left: [], right: ['actions'] });

  // Tree-mode expansion state. Uncontrolled by default; if the caller passes
  // both `expanded` and `onExpandedChange`, the table becomes controlled.
  const [localExpanded, setLocalExpanded] = useState<ExpandedState>({});
  const expandedState = tree ? (tree.expanded ?? localExpanded) : undefined;
  const setExpandedSideEffect = tree?.onExpandedChange ?? setLocalExpanded;

  // Actions popover state - managed internally for table controls in toolbar
  const [actionsPopoverOpen, setActionsPopoverOpen] = useState(false);
  const [actionsPopoverContentState, setActionsPopoverContentState] =
    useState<ContentState>('menu');

  // Bulk edit popover state - managed separately from table controls
  const [bulkEditPopoverOpen, setBulkEditPopoverOpen] = useState(false);

  useEffect(() => {
    setSorting(initialState.sorting);
    setColumVisibility(initialState.columnVisibility);
    setColumnOrder(initialState.columnOrder);
  }, [initialState]);

  const handleReset = useCallback(() => {
    onConfigChange?.(null);
  }, [onConfigChange]);

  const table = useReactTable({
    data,
    _features: [DataTableFeatures],
    columns: _columns,
    enableMultiSort: true,
    enableMultiRowSelection: true,
    enableRowSelection: true,
    maxMultiSortColCount: 4,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...(tree
      ? {
          getSubRows: tree.getSubRows,
          getExpandedRowModel: getExpandedRowModel(),
          onExpandedChange: setExpandedSideEffect,
        }
      : {}),
    onColumnOrderChange: setColumnOrderSideEffect,
    onColumnVisibilityChange: setColumVisibilitySideEffect,
    onSortingChange: setSortingSideEffect,
    onRowSelectionChange,
    getRowId,
    state: {
      sorting,
      columnVisibility,
      columnOrder,
      columnPinning,
      rowSelection: rowSelection || {},
      ...(tree ? { expanded: expandedState } : {}),
    },
  });

  // Notify parent of sorted data changes (for PDF export, etc.)
  // Use a ref to track previous sorted IDs and avoid unnecessary callback invocations
  const prevSortedIdsRef = useRef<string>('');
  useEffect(() => {
    if (!onSortedDataChange) return;

    const sortedRows = table.getSortedRowModel().rows;
    const sortedIds = sortedRows.map((row) => row.id).join(',');

    // Only call callback if the sorted order actually changed
    if (sortedIds !== prevSortedIdsRef.current) {
      prevSortedIdsRef.current = sortedIds;
      const sortedData = sortedRows.map((row) => row.original);
      onSortedDataChange(sortedData);
    }
  }, [sorting, data, onSortedDataChange, table]);

  const anyRowsSelected =
    table.getIsSomeRowsSelected() || table.getIsAllRowsSelected();

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const rows = table.getRowModel().rows;
    if (!rows.length) return;

    switch (e.key) {
      case 'ArrowUp':
      case 'ArrowDown': {
        e.preventDefault();
        const direction = e.key === 'ArrowUp' ? -1 : 1;
        const newIndex =
          activeRowIndex === null
            ? direction === -1
              ? rows.length - 1
              : 0
            : Math.max(
                0,
                Math.min(rows.length - 1, activeRowIndex + direction),
              );

        setActiveRowIndex(newIndex);

        // If shift is pressed, toggle selection of the new row
        if (e.shiftKey) {
          const selected = rows[activeRowIndex!]?.getIsSelected();
          rows[newIndex].toggleSelected(selected);
        }
        break;
      }
      case 'Enter':
        e.preventDefault();
        if (activeRowIndex !== null) {
          // Activate the row. Mirrors row-body click: in expand-primary tree
          // mode an expandable row toggles in place; otherwise navigate.
          // Opens in current context without losing table focus
          const activeRow = rows[activeRowIndex];
          if (expandOnRowClick && activeRow.getCanExpand()) {
            activeRow.toggleExpanded();
          } else {
            navigate({ to: createURIPath(activeRow.original) });
          }
        }
        break;
      case ' ':
        e.preventDefault();
        if (activeRowIndex !== null) {
          rows[activeRowIndex].toggleSelected();
        }
        break;
      case 'b':
      case 'B':
        // Open bulk edit mode for selected items
        e.preventDefault();
        if (!e.ctrlKey && anyRowsSelected && renderBulkEdit) {
          setBulkEditPopoverOpen(true);
        }
        break;
      case 'c':
      case 'C':
        // Open column visibility/configuration menu
        e.preventDefault();
        setActionsPopoverOpen(true);
        setActionsPopoverContentState('columns');
        break;
      case 's':
      case 'S':
        // Open sort options
        e.preventDefault();
        setActionsPopoverOpen(true);
        setActionsPopoverContentState('sorting');
        break;
      case 'n':
      case 'N':
        // Create new item/row - delegate to parent via callback
        e.preventDefault();
        onNew?.();
        break;
      default:
        break;
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <TableSearchBar
        isFiltering={isFiltering}
        resultCount={resultCount}
        totalCount={totalCount}
        entityName={entityName}
        filterValue={filterValue}
        onFilterChange={onFilterChange}
        tokenDefinitions={tokenDefinitions}
        defaultFilter={defaultFilter}
        addPath={addPath}
        addDisabled={addDisabled}
        table={table}
        treeMode={!!tree}
        tableControlsOpen={actionsPopoverOpen}
        onTableControlsOpenChange={setActionsPopoverOpen}
        tableControlsContentState={actionsPopoverContentState}
        onTableControlsContentStateChange={setActionsPopoverContentState}
        onTableReset={handleReset}
      />
      <div className="rounded-lg border">
        <StickyScrollbar>
          <TableComponent
            className="group/table"
            containerClassName="[scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            data-select-mode={anyRowsSelected ? 'active' : 'inactive'}
            tabIndex={0}
            onKeyDown={handleKeyDown}
            role="grid"
            aria-activedescendant={
              activeRowIndex !== null
                ? table.getRowModel().rows[activeRowIndex].id
                : undefined
            }
          >
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => {
                // Split headers for mobile
                const allHeaders = headerGroup.headers;
                const summaryHeaders = isMobile
                  ? allHeaders
                      .filter((h) => h.id !== 'actions')
                      .slice(0, MOBILE_SUMMARY_COLUMN_COUNT)
                  : [];
                const remainingHeaders = isMobile
                  ? allHeaders.filter(
                      (h) =>
                        h.id === 'actions' ||
                        !summaryHeaders.some((sh) => sh.id === h.id),
                    )
                  : allHeaders;

                return (
                  <TableRow key={headerGroup.id} className="group/row">
                    {/* Mobile: Summary header with column names and sort dropdown */}
                    {isMobile && summaryHeaders.length > 0 && (
                      <TableHead className="group/cell z[0] relative">
                        <MobileSummaryHeaderCell headers={summaryHeaders} />
                      </TableHead>
                    )}
                    {/* Remaining headers (or all on desktop) */}
                    {remainingHeaders.map((header) => (
                      <TableHead
                        key={header.id}
                        className={cn(
                          'group/cell',
                          header.column.getIsPinned() === 'right'
                            ? 'sticky right-0'
                            : 'z[0] relative',
                        )}
                      >
                        {header.id !== 'actions' ? (
                          <TableHeaderCell table={table} header={header} />
                        ) : (
                          <TableSelectionHeader table={table} />
                        )}
                      </TableHead>
                    ))}
                  </TableRow>
                );
              })}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <LoadingTableRow numColumns={columns.length} />
              ) : !table.getRowModel().rows?.length ? (
                <NoDataTableRow numColumns={columns.length} />
              ) : (
                table.getRowModel().rows.map((row, index) => {
                  return (
                    <RowMemo
                      selected={row.getIsSelected()}
                      active={
                        index === activeRowIndex ||
                        (activeRowId != null && row.id === String(activeRowId))
                      }
                      key={row.id}
                      row={row}
                      cells={row.getVisibleCells()}
                      index={index}
                      navigate={navigate}
                      createURIPath={createURIPath}
                      createShareContent={createShareContent}
                      actions={actions}
                      table={table}
                      isMobile={isMobile}
                      summaryWidth={summaryWidth}
                      hoveredRowId={hoveredRowId}
                      onRowHover={onRowHover}
                      expandOnRowClick={expandOnRowClick}
                      treeIndentPerLevelPx={treeIndentPerLevelPx}
                      treeLeadingPrefixPx={treeLeadingPrefix}
                    />
                  );
                })
              )}
            </TableBody>
          </TableComponent>
        </StickyScrollbar>
      </div>

      {/* Sticky bulk action bar - appears when rows are selected */}
      <StickyBulkActionBar
        table={table}
        renderBulkEdit={renderBulkEdit}
        open={bulkEditPopoverOpen}
        onOpenChange={setBulkEditPopoverOpen}
        actions={actions}
      />
    </div>
  );
}

const RowMemo = memo(InnerRow) as typeof InnerRow;

/**
 * Creates a styled drag preview element for drag-and-drop operations
 */
function createDragPreview(
  content: string,
  options?: { maxWidth?: string },
): HTMLElement {
  const preview = document.createElement('div');
  preview.textContent = content;
  preview.style.padding = '8px 12px';
  preview.style.background = '#dcfce7';
  preview.style.color = '#15803d';
  preview.style.borderRadius = '4px';
  preview.style.fontSize = '12px';
  preview.style.fontWeight = '500';
  preview.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';

  if (options?.maxWidth) {
    preview.style.maxWidth = options.maxWidth;
    preview.style.overflow = 'hidden';
    preview.style.textOverflow = 'ellipsis';
    preview.style.whiteSpace = 'nowrap';
  }

  return preview;
}

function InnerRow<TData>({
  row,
  cells,
  index,
  selected,
  active,
  navigate,
  createURIPath,
  createShareContent,
  actions: actionsProp,
  table,
  isMobile,
  summaryWidth,
  hoveredRowId,
  onRowHover,
  expandOnRowClick = false,
  treeIndentPerLevelPx = 0,
  treeLeadingPrefixPx = 0,
}: {
  row: Row<TData>;
  cells: ReturnType<Row<TData>['getVisibleCells']>;
  index: number;
  selected: boolean;
  active: boolean;
  navigate: (opts: { to: string }) => void;
  createURIPath: (data: TData) => string;
  createShareContent?: (data: TData, url: string) => any;
  actions: RowAction<TData>[] | ((data: TData) => RowAction<TData>[]);
  table: any;
  isMobile: boolean;
  summaryWidth: number | null;
  hoveredRowId?: string | number | null;
  onRowHover?: (rowId: string | number | null) => void;
  expandOnRowClick?: boolean;
  treeIndentPerLevelPx?: number;
  treeLeadingPrefixPx?: number;
}) {
  // Resolve actions - can be array or function
  const actions =
    typeof actionsProp === 'function' ? actionsProp(row.original) : actionsProp;
  const { isOpen, content, openShareDialog, setOpen } = useShareDialog();
  // Store drag preview element for cleanup
  let dragPreviewElement: HTMLElement | null = null;

  const handleDragStart = (e: React.DragEvent<HTMLTableRowElement>) => {
    e.dataTransfer.effectAllowed = 'copy';

    // Check if we have multiple selected rows
    const hasSelection =
      table.getIsSomeRowsSelected() || table.getIsAllRowsSelected();

    // If we're dragging a selected row and there are multiple selections, use all selected
    // Otherwise, just use the current row
    const shouldDragMultiple =
      hasSelection && table.getSelectedRowModel().rows.length > 1 && selected;

    if (shouldDragMultiple) {
      // Multi-entity drag
      // Get rows in their visual table order (respects sorting/filtering)
      // then filter to only selected rows to maintain visual order
      const allRows = table.getRowModel().rows;
      const sortedRows = allRows.filter((r: any) => r.getIsSelected());
      const entities = sortedRows.map((r: Row<TData>) => {
        const data = r.original as any;
        return {
          id: data.id,
          number: data.number || null,
          title: data.title || data.name || '',
          // Entity type detection heuristic:
          // 1. Use explicit 'type' field if present
          // 2. Use 'entityType' field if present
          // 3. If entity has a 'number' field, assume it's a ticket
          // 4. Otherwise, fall back to generic 'item'
          // Note: For better type safety, consider requiring explicit entityType on draggable rows
          entityType:
            data.type ||
            data.entityType ||
            (data.number !== undefined ? 'ticket' : 'item'),
        };
      });

      // Internal format for app
      e.dataTransfer.setData(
        'application/x-entities',
        JSON.stringify(entities),
      );

      // Plain text format for external apps
      e.dataTransfer.setData(
        'text/plain',
        JSON.stringify(
          sortedRows.map(({ original }: any) => original),
          null,
          2,
        ),
      );

      // Create drag preview showing count
      dragPreviewElement = createDragPreview(`${sortedRows.length} items`);
      document.body.appendChild(dragPreviewElement);
      e.dataTransfer.setDragImage(dragPreviewElement, 0, 0);
    } else {
      // Single entity drag
      const rowData = row.original as any;

      const dragPayload = {
        id: rowData.id,
        number: rowData.number || null,
        title: rowData.title || rowData.name || '',
        // Entity type detection heuristic:
        // 1. Use explicit 'type' field if present
        // 2. Use 'entityType' field if present
        // 3. If entity has a 'number' field, assume it's a ticket
        // 4. Otherwise, fall back to generic 'item'
        // Note: For better type safety, consider requiring explicit entityType on draggable rows
        entityType:
          rowData.type ||
          rowData.entityType ||
          (rowData.number !== undefined ? 'ticket' : 'item'),
      };

      // Internal format for app
      e.dataTransfer.setData(
        'application/x-entity',
        JSON.stringify(dragPayload),
      );

      e.dataTransfer.setData('text/plain', JSON.stringify(rowData, null, 2));

      // Create drag preview element
      const displayText = dragPayload.number
        ? `#${dragPayload.number} ${dragPayload.title}`
        : `#${dragPayload.id.slice(0, 8)} ${dragPayload.title}`;
      dragPreviewElement = createDragPreview(displayText, {
        maxWidth: '300px',
      });
      document.body.appendChild(dragPreviewElement);
      e.dataTransfer.setDragImage(dragPreviewElement, 0, 0);
    }
  };

  const handleDragEnd = () => {
    // Clean up drag preview element
    if (dragPreviewElement && document.body.contains(dragPreviewElement)) {
      document.body.removeChild(dragPreviewElement);
      dragPreviewElement = null;
    }
  };

  // Handle navigation on row click
  const handleNavigationClick = (event: TouchEvent | MouseEvent) => {
    // Don't navigate if clicking on a button or interactive element
    const target = event.target as HTMLElement;
    if (target.closest('button, a, input, select, textarea')) {
      return;
    }
    // Expand-primary tree mode: expandable rows toggle in place; drilling
    // moves to the dedicated disclosure button in the actions cell. Leaf
    // rows fall through to navigation as usual. Alt-click toggles the whole
    // subtree, mirroring the chevron.
    if (expandOnRowClick && row.getCanExpand()) {
      if ('altKey' in event && event.altKey) {
        setSubtreeExpanded(row, !row.getIsExpanded());
      } else {
        row.toggleExpanded();
      }
      return;
    }
    navigate({ to: createURIPath(row.original) });
  };

  // Build unified actions array
  const unifiedActions: UnifiedAction<TData>[] = [
    // Tree subtree expand/collapse (only for expandable rows). getCanExpand
    // is false on non-tree tables, so this is naturally scoped to tree mode.
    ...(row.getCanExpand()
      ? [
          {
            label: row.getIsExpanded()
              ? 'Collapse all under'
              : 'Expand all under',
            icon: row.getIsExpanded() ? ChevronsDownUp : ChevronsUpDown,
            onAction: () => {
              setSubtreeExpanded(row, !row.getIsExpanded());
            },
          },
          { type: 'separator' as const },
        ]
      : []),

    // Selection action
    ...(row.getCanSelect()
      ? [
          {
            label: row.getIsSelected() ? 'Deselect' : 'Select',
            icon: row.getIsSelected() ? Square : SquareCheckBig,
            onAction: () => {
              row.getToggleSelectedHandler()({} as any);
            },
          },
        ]
      : []),

    // Share action
    ...(createShareContent
      ? [
          {
            label: 'Share',
            icon: Share2,
            onAction: (data: TData) => {
              const url = `${window.location.origin}${createURIPath(data)}`;
              const shareContent = createShareContent(data, url);
              openShareDialog(shareContent);
            },
          },
          { type: 'separator' as const },
        ]
      : []),

    // Custom row actions
    ...actions
      .filter((action) => {
        if (!action.hideWhenDisabled) return true;
        const d = action.disabled;
        const isDisabled = typeof d === 'function' ? d(row.original) : d;
        return !isDisabled;
      })
      .map((action) => ({
        label: action.label,
        sublabel: action.sublabel,
        icon: action.icon,
        onAction: action.onCalled,
        disabled: action.disabled,
      })),
  ];

  // Split cells for mobile: first 3 (excluding actions) become summary, rest are normal
  const summaryCells = isMobile
    ? cells
        .filter((c) => c.column.id !== 'actions')
        .slice(0, MOBILE_SUMMARY_COLUMN_COUNT)
    : [];
  const remainingCells = isMobile
    ? cells.filter(
        (c) =>
          c.column.id === 'actions' ||
          !summaryCells.some((sc) => sc.id === c.id),
      )
    : cells;

  // Get row identifier for hover state (try recnum, id, or row.id)
  const rowId =
    (row.original as any).recnum ?? (row.original as any).id ?? row.id;
  const isHovered = hoveredRowId === rowId;

  const rowContent = (
    <TableRow
      data-state={selected && 'selected'}
      data-active={active && 'active'}
      data-row-id={index}
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onMouseEnter={() => onRowHover?.(rowId)}
      onMouseLeave={() => onRowHover?.(null)}
      className={cn(
        'group/row',
        active && 'bg-muted/50 underline',
        // Show hover state when menu is open
        'data-[menu-open]:bg-muted/50',
        // Highlight when hovered from external source (e.g., map)
        isHovered && 'bg-muted/40',
      )}
      id={row.id}
      role="row"
    >
      {/* Mobile: Render collapsed summary cell for first 3 columns */}
      {isMobile && summaryCells.length > 0 && (
        <TableCell role="gridcell" className="z[0] relative">
          <MobileSummaryCell
            cells={summaryCells}
            width={summaryWidth}
            indentPerLevelPx={treeIndentPerLevelPx}
            leadingPrefixPx={treeLeadingPrefixPx}
          />
        </TableCell>
      )}
      {/* Remaining cells (or all cells on desktop) */}
      {remainingCells.map((cell) => {
        return (
          <TableCell
            key={cell.id}
            role="gridcell"
            className={cn(
              cell.column.id === 'actions' && 'relative p-0',
              cell.column.getIsPinned() === 'right'
                ? 'sticky right-0'
                : 'z[0] relative',
            )}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        );
      })}
    </TableRow>
  );

  return (
    <>
      <UnifiedContextMenu
        actions={unifiedActions}
        data={row.original}
        onClick={handleNavigationClick}
      >
        {rowContent}
      </UnifiedContextMenu>

      {/* Share dialog */}
      {content && (
        <ShareDialog open={isOpen} onOpenChange={setOpen} content={content} />
      )}
    </>
  );
}
